import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  AUDIENCE,
  LICENSE_CATEGORY,
  DOCUMENT,
} from "./consts.ts";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  addSchema,
  AddHelpDocumentFormValues,
} from "./validations.ts";
import { useEffect, useRef, useState } from "react";
import { toast } from "@/components/Toast";
import { useAddHelpDocumentMutation, useGetAttachmentById, useGetHelpDocumentById, useUpdateHelpDocumentMutation } from "@/services";
import {
  capitalize,
  getBaseRoute,
  getRequiredFields,
} from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
const useAddEdit = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const basePath = getBaseRoute(useLocation().pathname);
  const attachmentRef = useRef<HTMLInputElement>(null);
  const {id, action} = useParams();
  const isView = action == "view";

  const [keys, _setKeys] = useState<any>({
    audience: Math.random() * 2
  })

  const form = useForm<AddHelpDocumentFormValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      [AUDIENCE]: "",
      [LICENSE_CATEGORY]: "",
      [DOCUMENT]: undefined,
    },
  });

  const requiredFields = getRequiredFields(addSchema);

  const breadcrumbData = [
    {
      link: `/${basePath}/${routes.masterData}/help-document`,
      name: "Help Documents",
    },
    {
      link: "",
      name: `${capitalize(action || "")} Help Document`,
    },
  ];

  const onBackClick = () => {
    navigate(`/${basePath}/${routes.masterData}/help-document`);
  };

  const {data: helpDocData, isLoading: isLoadingHelpDocData} = useGetHelpDocumentById({
    id: id as string,
    queryParams: {
      enabled: !!id
    }
  });

  useEffect(() => {
    if (helpDocData?.data) {
      form.reset({
        [AUDIENCE]: helpDocData?.data?.[AUDIENCE] || "",
        [LICENSE_CATEGORY]: helpDocData?.data?.[LICENSE_CATEGORY] || "",
        [DOCUMENT]: helpDocData?.data?.attachment ? { name: helpDocData?.data?.attachment?.originalFileName } as File : undefined,
      })
    }
  }, [helpDocData]);

  const { data: attachment, refetch: refetchAttachment, isRefetching: isRefetchingAttachment } =
    useGetAttachmentById({
      id: helpDocData?.data?.attachment?.attachmentId,
      queryParams: {
        enabled: false
      }
    });

  useEffect(() => {
    if (attachment?.data) {
      window.open(attachment?.data, "_blank")
      setTimeout(() => {
        queryClient.cancelQueries({queryKey: ["attachment-by-id", helpDocData?.data?.attachment?.attachmentId]})
      }, 100)
    }
  }, [attachment])

  const { mutate: addInfo, isPending: isAddPending } =
    useAddHelpDocumentMutation({
      onError: (error?: any) => {
        toast.error(
          error?.response?.data?.message ||
            "Something went wrong"
        );
      },
      onSuccess: () => {
        toast.success("Help Document Added Successfully");
        queryClient.invalidateQueries({
          queryKey: ["help-documents"],
        });
        onBackClick();
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateHelpDocumentMutation({
      onError: (error?: any) => {
        toast.error(
          error?.response?.data?.message ||
            "Something went wrong"
        );
      },
      onSuccess: () => {
        toast.success("Help Document Updated Successfully");
        queryClient.invalidateQueries({
          queryKey: ["help-documents"],
        });
        onBackClick();
      },
    });

  const onSubmit = (data: AddHelpDocumentFormValues) => {
    if (id) {
      updateInfo({id: id as string, data})
    } else {
      addInfo({ data });
    }
  };

  const onPreviewAttachment = () => {
    refetchAttachment();
  }

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
    attachmentRef,
    isView,
    keys,
    isUpdatePending,
    isLoadingHelpDocData,
    onPreviewAttachment,
    isRefetchingAttachment,
    id
  };
};

export default useAddEdit;
